/**
 * Shared vocabulary for reading stored times of day (currently an operator's
 * hours of operation, on `DateRangeISO.start`/`.end`).
 *
 * These values are ISO strings, but they represent a *wall-clock time of day*
 * rather than an instant: "12:00pm" means noon wherever the business is, on every
 * day it is open. There is no single moment to anchor them to, so they are pinned
 * to the Unix epoch date and the time is carried in the string.
 *
 * Two encodings exist in the data, and they disagree about *where in the string*
 * the intended time lives:
 *
 *  - **Canonical** — everything written since PR #170 (2024-12-02). Pinned to the
 *    epoch date, intended time in the **UTC** portion. Values written between that
 *    PR and the timezone fix carry the editing browser's offset — an intended
 *    12:00 saved in Mountain reads `1970-01-01T05:00:00-07:00` — but only the
 *    serialization was ever wrong, never the instant, so the UTC wall clock is
 *    12:00 for all of them. Midnight at a negative offset rolls the date back to
 *    1969-12-31; a late evening at a positive offset rolls it forward to
 *    1970-01-02.
 *
 *  - **Legacy** — written before PR #170. Stamped with the date the edit happened
 *    and the intended time in the **local** portion, e.g. `2024-11-26T13:00:00-05:00`
 *    for 1:00pm. Reading UTC would report 18:00 for these.
 *
 * The date component discriminates cleanly: canonical values are always within a
 * day of the epoch, legacy ones never are.
 *
 * This module is deliberately dependency-free and shared by both the frontend
 * (which re-serializes to canonical ISO for the hours editor) and the backend
 * (which projects to plain `HH:mm` for the API). Keeping the discriminator in one
 * place matters: if the two sides ever disagreed about which encoding a value is
 * in, they would silently report times that differ by the editor's UTC offset —
 * which is the exact bug this vocabulary exists to prevent.
 */

/** Dates a canonical, epoch-pinned time of day can legitimately land on. */
export const EPOCH_DATES = ["1969-12-31", "1970-01-01", "1970-01-02"] as const;

const EPOCH_DATE_SET: ReadonlySet<string> = new Set(EPOCH_DATES);

/** The wall-clock parts of a stored time of day, as written in the string. */
export interface StoredTimeOfDay {
  /** Date portion, e.g. `"1970-01-01"`. */
  date: string;
  /** Hour of the *local* portion of the string, 0-23. */
  hours: number;
  /** Minute of the *local* portion of the string, 0-59. */
  minutes: number;
  /**
   * Whether the value is epoch-pinned, and therefore carries its intended time
   * in the UTC portion rather than in `hours`/`minutes`.
   */
  isEpochPinned: boolean;
}

/**
 * Splits a stored time of day into its parts and classifies which encoding it
 * uses, so callers can read the intended time from the right half of the string.
 *
 * @returns the parsed parts, or `null` if the value is absent or unparseable.
 */
export const parseStoredTimeOfDay = (
  value?: string | null,
): StoredTimeOfDay | null => {
  if (!value) return null;

  const parsed = value.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (!parsed) return null;

  const [, date, hours, minutes] = parsed;

  return {
    date,
    hours: Number(hours),
    minutes: Number(minutes),
    isEpochPinned: EPOCH_DATE_SET.has(date),
  };
};
